import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Settings,
	TestTube,
	Save,
	RotateCcw,
	Eye,
	EyeOff,
	X,
	RefreshCw,
	Plus,
	Trash2,
} from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import styles from "./AdminSettings.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/settings",
	component: withPermission(AdminSettingsPage, "admin", "read"),
});

interface SettingRow {
	key: string;
	value: string | null;
	valueI18n: Record<string, string> | null;
	category: string;
	isSecret: boolean;
	description: string | null;
	updatedAt: string | null;
}

interface TestResult {
	type: "success" | "error";
	message: string;
}

interface AdminUserRow {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	isActive: boolean;
	role: string;
}

interface CompanyRow {
	id: string;
	legalName: string;
	sapCardCode: string | null;
	currency: string | null;
}

const DELEGATE_SETTING_KEY = "SALES_DELEGATE_USER_IDS";
const GLOBAL_DELEGATE_SETTING_KEY = "SALES_GLOBAL_DELEGATE_USER_IDS";
const PUBLIC_COMPANY_SETTING_KEY = "PUBLIC_COMPANY_IDS";
const LOAD_REPORT_RESPONSIBLE_SETTING_KEY = "LOAD_REPORT_RESPONSIBLE_USER_IDS";

function toggleCsvId(current: string | null, id: string): string | null {
	const ids = (current ?? "")
		.split(",")
		.map((v) => v.trim())
		.filter(Boolean);
	const next = ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id];
	return next.length > 0 ? next.join(",") : null;
}

function AdminSettingsPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [settings, setSettings] = useState<SettingRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
	const [testing, setTesting] = useState<Record<string, boolean>>({});
	const [revealed, setRevealed] = useState<Record<string, boolean>>({});
	const [revealing, setRevealing] = useState<Record<string, boolean>>({});
	const [saveStatus, setSaveStatus] = useState<TestResult | null>(null);
	const [termsLang, setTermsLang] = useState<"tr" | "en">("tr");
	const [allUsers, setAllUsers] = useState<AdminUserRow[]>([]);
	const [knownCompanies, setKnownCompanies] = useState<CompanyRow[]>([]);

	useEffect(() => {
		let cancelled = false;
		api
			.get<{ items: CompanyRow[] }>("/api/admin/settings/public-companies")
			.then((res) => {
				if (!cancelled) setKnownCompanies(res.items ?? []);
			})
			.catch(() => {
				if (!cancelled) setKnownCompanies([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		api
			.get<{ items: AdminUserRow[] }>("/api/admin/users")
			.then((res) => {
				if (!cancelled) setAllUsers(res.items ?? []);
			})
			.catch(() => {
				if (!cancelled) setAllUsers([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const toggleGlobalDelegate = (current: string | null, userId: string) => {
		updateValue(GLOBAL_DELEGATE_SETTING_KEY, toggleCsvId(current, userId));
	};

	const toggleLoadReportResponsible = (current: string | null, userId: string) => {
		updateValue(LOAD_REPORT_RESPONSIBLE_SETTING_KEY, toggleCsvId(current, userId));
	};

	const toggleGroupCompany = (current: string | null, company: CompanyRow) => {
		setKnownCompanies((prev) =>
			prev.some((c) => c.id === company.id) ? prev : [...prev, company],
		);
		updateValue(PUBLIC_COMPANY_SETTING_KEY, toggleCsvId(current, company.id));
	};

	useEffect(() => {
		if (user && user.role !== "admin") {
			window.location.href = "/";
		}
	}, [user]);

	useEffect(() => {
		let cancelled = false;
		api
			.get<SettingRow[]>("/api/admin/settings")
			.then((rows) => {
				if (!cancelled) {
					setSettings(rows);
					setLoading(false);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setSettings([]);
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const updateValue = (key: string, value: string | null) => {
		setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
		setSaveStatus(null);
	};

	const updateTermValue = (key: string, lang: "tr" | "en", value: string) => {
		setSettings((prev) =>
			prev.map((s) =>
				s.key === key ? { ...s, valueI18n: { ...(s.valueI18n ?? {}), [lang]: value } } : s,
			),
		);
		setSaveStatus(null);
	};

	const handleSave = async () => {
		setSaving(true);
		setSaveStatus(null);
		try {
			await api.patch(
				"/api/admin/settings",
				settings.map((s) => {
					if (s.category === "offer_terms") {
						const tr = s.valueI18n?.["tr"] ?? s.value ?? "";
						const en = s.valueI18n?.["en"] ?? "";
						return { key: s.key, value: tr || null, valueI18n: { tr, en } };
					}
					return { key: s.key, value: s.value };
				}),
			);
			setSaveStatus({ type: "success", message: t("saveSuccess") });
		} catch (err) {
			setSaveStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleTest = async (endpoint: string, key: string) => {
		setTesting((prev) => ({ ...prev, [key]: true }));
		setTestResults((prev) => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
		try {
			const result = await api.post<{ success: boolean; message: string }>(endpoint, {});
			setTestResults((prev) => ({ ...prev, [key]: { type: "success", message: result.message } }));
		} catch (err) {
			const message = err instanceof Error ? err.message : t("error");
			setTestResults((prev) => ({ ...prev, [key]: { type: "error", message } }));
		} finally {
			setTesting((prev) => ({ ...prev, [key]: false }));
		}
	};

	const grouped = settings.reduce<Record<string, SettingRow[]>>((acc, s) => {
		const cat = s.category;
		if (!acc[cat]) acc[cat] = [];
		acc[cat].push(s);
		return acc;
	}, {});
	for (const rows of Object.values(grouped)) {
		rows.sort((a, b) =>
			a.key === LOAD_REPORT_RESPONSIBLE_SETTING_KEY
				? 1
				: b.key === LOAD_REPORT_RESPONSIBLE_SETTING_KEY
					? -1
					: 0,
		);
	}

	const categoryOrder = [
		"general",
		"sap",
		"ollama",
		"mssql",
		"v1_sql",
		"smtp",
		"offer_terms",
		"contract_terms",
	];
	const categories = categoryOrder.filter((c) => grouped[c] && grouped[c].length > 0);

	if (loading) {
		return (
			<div className={styles.container}>
				<div className={styles.heading}>{t("adminSettingsTitle")}</div>
				<div className={styles.loading}>{t("loading")}</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles.headingRow}>
					<Settings size={22} />
					<h1 className={styles.heading}>{t("adminSettingsTitle")}</h1>
				</div>
				<div className={styles.actions}>
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={() => window.location.reload()}
						disabled={saving}
						title={t("reset")}
					>
						<RotateCcw size={14} />
						{t("reset")}
					</button>
					<button
						type="button"
						className={styles.primaryButton}
						onClick={handleSave}
						disabled={saving}
					>
						<Save size={14} />
						{saving ? t("saving") : t("save")}
					</button>
				</div>
			</div>

			{saveStatus && (
				<div
					className={`${styles.statusBanner} ${saveStatus.type === "success" ? styles.statusSuccess : styles.statusError}`}
				>
					{saveStatus.message}
				</div>
			)}

			{categories.map((cat) => (
				<section key={cat} className={styles.section}>
					<h2 className={styles.sectionTitle}>{t(`category_${cat}`)}</h2>
					{cat === "offer_terms" && (
						<div className={styles.termsLangRow}>
							<button
								type="button"
								className={[styles.langButton, termsLang === "tr" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setTermsLang("tr")}
							>
								TR
							</button>
							<button
								type="button"
								className={[styles.langButton, termsLang === "en" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setTermsLang("en")}
							>
								EN
							</button>
						</div>
					)}
					<div className={styles.fields}>
						{grouped[cat]?.map((s) => (
							<div key={s.key} className={styles.field}>
								<label className={styles.label} htmlFor={s.key}>
									{s.key}
									{s.key === LOAD_REPORT_RESPONSIBLE_SETTING_KEY ? (
										<span className={styles.hint}>Yük Raporu Sorumlusu</span>
									) : s.key === GLOBAL_DELEGATE_SETTING_KEY ? (
										<span className={styles.hint}>
											Herkes adına teklif/sipariş girebilecek kişiler
										</span>
									) : (
										s.description && <span className={styles.hint}>{s.description}</span>
									)}
								</label>
								<div className={styles.inputWrap}>
									{s.key === PUBLIC_COMPANY_SETTING_KEY ? (
										<CompanyMultiSelect
											value={s.value}
											known={knownCompanies}
											onToggle={(company) => toggleGroupCompany(s.value, company)}
										/>
									) : s.key === DELEGATE_SETTING_KEY ? (
										<DelegationEditor
											users={allUsers}
											value={s.value}
											onChange={(value) => updateValue(DELEGATE_SETTING_KEY, value)}
										/>
									) : s.key === GLOBAL_DELEGATE_SETTING_KEY ? (
										<UserMultiSelect
											users={allUsers.filter((u) => u.role !== "admin")}
											value={s.value}
											onToggle={(userId) => toggleGlobalDelegate(s.value, userId)}
											hideInactive
											placeholder="Kullanıcı ara..."
										/>
									) : s.key === LOAD_REPORT_RESPONSIBLE_SETTING_KEY ? (
										<UserMultiSelect
											users={allUsers.filter((u) => u.role === "user")}
											value={s.value}
											onToggle={(userId) => toggleLoadReportResponsible(s.value, userId)}
											hideInactive
											placeholder="Kullanıcı ara..."
										/>
									) : cat === "offer_terms" ? (
										<textarea
											id={s.key}
											className={styles.textarea}
											value={
												(termsLang === "tr"
													? (s.valueI18n?.["tr"] ?? s.value)
													: s.valueI18n?.["en"]) ?? ""
											}
											onChange={(e) => updateTermValue(s.key, termsLang, e.target.value)}
											placeholder={t("notConfigured")}
											rows={4}
										/>
									) : cat === "contract_terms" ? (
										<textarea
											id={s.key}
											className={styles.textarea}
											value={s.value ?? ""}
											onChange={(e) => updateValue(s.key, e.target.value)}
											placeholder={t("notConfigured")}
											rows={4}
										/>
									) : (
										<input
											id={s.key}
											type={s.isSecret && !revealed[s.key] ? "password" : "text"}
											className={styles.input}
											value={s.value ?? ""}
											onChange={(e) => updateValue(s.key, e.target.value || null)}
											placeholder={t("notConfigured")}
										/>
									)}
									{s.isSecret && (
										<button
											type="button"
											className={styles.revealButton}
											onClick={async () => {
												if (s.isSecret && s.value === "••••••••" && !revealed[s.key]) {
													setRevealing((prev) => ({ ...prev, [s.key]: true }));
													try {
														const result = await api.get<{
															success: boolean;
															value: string | null;
														}>(`/api/admin/settings/${s.key}/reveal`);
														updateValue(s.key, result.value);
													} catch {
														// leave mask in place on error
													} finally {
														setRevealing((prev) => ({ ...prev, [s.key]: false }));
													}
												}
												setRevealed((prev) => ({ ...prev, [s.key]: !prev[s.key] }));
											}}
											tabIndex={-1}
										>
											{revealing[s.key] ? (
												<span className={styles.spinner} />
											) : revealed[s.key] ? (
												<EyeOff size={16} />
											) : (
												<Eye size={16} />
											)}
										</button>
									)}
								</div>
							</div>
						))}
					</div>
					<div className={styles.testRow}>
						{cat === "smtp" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-smtp", "smtp")}
								disabled={testing["smtp"]}
							>
								<TestTube size={14} />
								{testing["smtp"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "sap" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-sap", "sap")}
								disabled={testing["sap"]}
							>
								<TestTube size={14} />
								{testing["sap"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "ollama" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-ollama", "ollama")}
								disabled={testing["ollama"]}
							>
								<TestTube size={14} />
								{testing["ollama"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "v1_sql" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-v1", "v1_sql")}
								disabled={testing["v1_sql"]}
							>
								<TestTube size={14} />
								{testing["v1_sql"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{testResults[cat] && (
							<span
								className={`${styles.testResult} ${testResults[cat].type === "success" ? styles.testSuccess : styles.testError}`}
							>
								{testResults[cat].message}
							</span>
						)}
					</div>
				</section>
			))}

			<RegionsSection users={allUsers} />

			<CompanyBulkTransferSection users={allUsers} />

			<SapOrderOverrideSection />
		</div>
	);
}

interface OwnerSummaryRow {
	id: string;
	name: string;
	isActive: boolean;
	count: number;
	offerCount: number;
	orderCount: number;
}

function CompanyBulkTransferSection({ users }: { users: AdminUserRow[] }) {
	const { t } = useTranslation();
	const [owners, setOwners] = useState<OwnerSummaryRow[]>([]);
	const [fromUserId, setFromUserId] = useState("");
	const [toUserId, setToUserId] = useState("");
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<TestResult | null>(null);

	const loadOwners = () =>
		api
			.get<OwnerSummaryRow[]>("/api/companies/owner-summary")
			.then(setOwners)
			.catch(() => setOwners([]));

	useEffect(() => {
		void loadOwners();
	}, []);

	const source = owners.find((o) => o.id === fromUserId);
	const target = users.find((u) => u.id === toUserId);
	const targets = users
		.filter((u) => u.isActive && u.id !== fromUserId)
		.sort((a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, "tr"),
		);

	const run = async () => {
		if (!source || !target || running) return;
		const targetName = `${target.firstName} ${target.lastName}`.trim();
		setRunning(true);
		setResult(null);
		let preview: { companies: number; offers: number; orders: number };
		try {
			preview = await api.get(
				`/api/companies/bulk-transfer/preview?fromUserId=${encodeURIComponent(fromUserId)}`,
			);
		} catch (err) {
			setResult({ type: "error", message: err instanceof Error ? err.message : t("error") });
			setRunning(false);
			return;
		}
		if (
			!window.confirm(
				t("bulkTransferConfirm", {
					count: preview.companies,
					offers: preview.offers,
					orders: preview.orders,
					from: source.name,
					to: targetName,
				}),
			)
		) {
			setRunning(false);
			return;
		}
		try {
			const res = await api.post<{
				transferred: number;
				sapQueued: number;
				offersTransferred: number;
				ordersTransferred: number;
				documentsSapQueued: number;
			}>("/api/companies/bulk-transfer", { fromUserId, toUserId });
			setResult({
				type: "success",
				message: t("bulkTransferDone", {
					count: res.transferred,
					sap: res.sapQueued,
					offers: res.offersTransferred,
					orders: res.ordersTransferred,
					docsSap: res.documentsSapQueued,
				}),
			});
			setFromUserId("");
			setToUserId("");
			await loadOwners();
		} catch (err) {
			setResult({ type: "error", message: err instanceof Error ? err.message : t("error") });
		} finally {
			setRunning(false);
		}
	};

	return (
		<section className={styles.section}>
			<h2 className={styles.sectionTitle}>{t("bulkTransferTitle")}</h2>
			<div className={styles.fields}>
				<div className={styles.field}>
					<label className={styles.label}>
						{t("bulkTransferFrom")}
						<span className={styles.hint}>{t("bulkTransferHint")}</span>
					</label>
					<div className={styles.inputWrap}>
						<SearchableSelect
							value={fromUserId}
							onChange={(v) => setFromUserId(v)}
							options={owners.map((o) => ({
								value: o.id,
								label: `${o.name}${o.isActive ? "" : ` (${t("inactive")})`} — ${t("bulkTransferOwnerCounts", { companies: o.count, offers: o.offerCount, orders: o.orderCount })}`,
							}))}
							placeholder={t("selectOption")}
							clearable
						/>
					</div>
				</div>
				<div className={styles.field}>
					<label className={styles.label}>{t("bulkTransferTo")}</label>
					<div className={styles.inputWrap}>
						<SearchableSelect
							value={toUserId}
							onChange={(v) => setToUserId(v)}
							options={targets.map((u) => ({
								value: u.id,
								label: `${u.firstName} ${u.lastName}`.trim(),
							}))}
							placeholder={t("selectOption")}
							clearable
						/>
					</div>
				</div>
			</div>
			<div className={styles.testRow}>
				<button
					type="button"
					className={styles.testButton}
					onClick={() => void run()}
					disabled={running || !source || !target}
				>
					<RefreshCw size={14} />
					{running ? t("bulkTransferRunning") : t("bulkTransferAction")}
				</button>
				{result && (
					<span
						className={`${styles.testResult} ${result.type === "success" ? styles.testSuccess : styles.testError}`}
					>
						{result.message}
					</span>
				)}
			</div>
		</section>
	);
}

function SapOrderOverrideSection() {
	const { t } = useTranslation();
	const [docNum, setDocNum] = useState("");
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<TestResult | null>(null);

	const run = async () => {
		const value = docNum.trim();
		if (!value || running) return;
		if (!window.confirm(t("sapOrderOverrideConfirm", { docNum: value }))) return;
		setRunning(true);
		setResult(null);
		try {
			const res = await api.post<{ success: boolean; message: string }>(
				"/api/admin/settings/orders/override-from-sap",
				{ docNum: value },
			);
			setResult({ type: "success", message: res.message });
		} catch (err) {
			setResult({ type: "error", message: err instanceof Error ? err.message : t("error") });
		} finally {
			setRunning(false);
		}
	};

	return (
		<section className={styles.section}>
			<h2 className={styles.sectionTitle}>{t("sapOrderOverrideTitle")}</h2>
			<div className={styles.fields}>
				<div className={styles.field}>
					<label className={styles.label} htmlFor="sap-order-override-docnum">
						{t("sapOrderOverrideLabel")}
						<span className={styles.hint}>{t("sapOrderOverrideHint")}</span>
					</label>
					<div className={styles.inputWrap}>
						<input
							id="sap-order-override-docnum"
							type="text"
							inputMode="numeric"
							className={styles.input}
							value={docNum}
							onChange={(e) => setDocNum(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") void run();
							}}
							placeholder="12345"
						/>
					</div>
				</div>
			</div>
			<div className={styles.testRow}>
				<button
					type="button"
					className={styles.testButton}
					onClick={() => void run()}
					disabled={running || docNum.trim().length === 0}
				>
					<RefreshCw size={14} />
					{running ? t("sapOrderOverrideRunning") : t("sapOrderOverrideAction")}
				</button>
				{result && (
					<span
						className={`${styles.testResult} ${result.type === "success" ? styles.testSuccess : styles.testError}`}
					>
						{result.message}
					</span>
				)}
			</div>
		</section>
	);
}

function CompanyMultiSelect({
	value,
	known,
	onToggle,
}: {
	value: string | null;
	known: CompanyRow[];
	onToggle: (company: CompanyRow) => void;
}) {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [matches, setMatches] = useState<CompanyRow[]>([]);
	const [searching, setSearching] = useState(false);

	const selectedIds = (value ?? "")
		.split(",")
		.map((id) => id.trim())
		.filter(Boolean);
	const selected = selectedIds
		.map((id) => known.find((c) => c.id === id))
		.filter((c): c is CompanyRow => !!c);

	useEffect(() => {
		const term = query.trim();
		let cancelled = false;
		const timer = setTimeout(() => {
			if (term.length < 2) {
				setMatches([]);
				setSearching(false);
				return;
			}
			setSearching(true);
			api
				.get<{ items: CompanyRow[] }>(
					`/api/companies?all=true&status=customer&limit=20&search=${encodeURIComponent(term)}`,
				)
				.then((res) => {
					if (!cancelled) setMatches(res.items ?? []);
				})
				.catch(() => {
					if (!cancelled) setMatches([]);
				})
				.finally(() => {
					if (!cancelled) setSearching(false);
				});
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	return (
		<div className={styles.multiSelect}>
			{selected.length > 0 && (
				<div className={styles.chipRow}>
					{selected.map((c) => (
						<span key={c.id} className={styles.chip}>
							{c.currency ? `${c.legalName} (${c.currency})` : c.legalName}
							<button
								type="button"
								className={styles.chipRemove}
								onClick={() => onToggle(c)}
								aria-label={t("remove")}
							>
								<X size={13} />
							</button>
						</span>
					))}
				</div>
			)}
			<input
				type="text"
				className={styles.input}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder={t("searchCompanyPlaceholder")}
			/>
			{query.trim().length >= 2 && (
				<div className={styles.optionList}>
					{searching ? (
						<span className={styles.hint}>{t("loading")}</span>
					) : matches.filter((c) => !selectedIds.includes(c.id)).length === 0 ? (
						<span className={styles.hint}>{t("noRecords")}</span>
					) : (
						matches
							.filter((c) => !selectedIds.includes(c.id))
							.map((c) => (
								<button
									key={c.id}
									type="button"
									className={styles.option}
									onClick={() => {
										onToggle(c);
										setQuery("");
									}}
								>
									<span>{c.legalName}</span>
									<span className={styles.optionMeta}>
										{[c.currency, c.sapCardCode].filter(Boolean).join(" · ")}
									</span>
								</button>
							))
					)}
				</div>
			)}
		</div>
	);
}

function UserMultiSelect({
	users,
	value,
	onToggle,
	hideInactive,
	placeholder,
}: {
	users: AdminUserRow[];
	value: string | null;
	onToggle: (userId: string) => void;
	hideInactive?: boolean;
	placeholder?: string;
}) {
	const selectedIds = (value ?? "")
		.split(",")
		.map((id) => id.trim())
		.filter(Boolean);
	return (
		<UserPicker
			users={users}
			selectedIds={selectedIds}
			onToggle={onToggle}
			{...(hideInactive !== undefined ? { hideInactive } : {})}
			{...(placeholder !== undefined ? { placeholder } : {})}
		/>
	);
}

type DelegationMap = Record<string, string[]>;

/** Eski virgüllü liste okunursa vekiller yetkili kişisi olmadan gelir. */
function parseDelegationMap(value: string | null): DelegationMap {
	if (!value?.trim()) return {};
	try {
		const parsed: unknown = JSON.parse(value);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
		return Object.fromEntries(
			Object.entries(parsed).map(([id, principals]) => [
				id,
				Array.isArray(principals)
					? principals.filter((p): p is string => typeof p === "string")
					: [],
			]),
		);
	} catch {
		return Object.fromEntries(
			value
				.split(",")
				.map((id) => id.trim())
				.filter(Boolean)
				.map((id) => [id, []]),
		);
	}
}

function DelegationEditor({
	users,
	value,
	onChange,
}: {
	users: AdminUserRow[];
	value: string | null;
	onChange: (value: string | null) => void;
}) {
	const { t } = useTranslation();
	const map = parseDelegationMap(value);
	const delegateIds = Object.keys(map);

	const update = (next: DelegationMap) =>
		onChange(Object.keys(next).length > 0 ? JSON.stringify(next) : null);
	const remove = (delegateId: string) => {
		const next = { ...map };
		delete next[delegateId];
		update(next);
	};
	const togglePrincipal = (delegateId: string, userId: string) => {
		const current = map[delegateId] ?? [];
		update({
			...map,
			[delegateId]: current.includes(userId)
				? current.filter((id) => id !== userId)
				: [...current, userId],
		});
	};

	return (
		<div className={styles.multiSelect}>
			{delegateIds.map((delegateId) => {
				const delegate = users.find((u) => u.id === delegateId);
				const name = delegate ? `${delegate.firstName} ${delegate.lastName}`.trim() : delegateId;
				return (
					<div key={delegateId} className={styles.delegateRow}>
						<div className={styles.delegateHeader}>
							<span>{delegate && !delegate.isActive ? `${name} (${t("inactive")})` : name}</span>
							<button
								type="button"
								className={styles.chipRemove}
								onClick={() => remove(delegateId)}
								title={t("remove")}
								aria-label={t("remove")}
							>
								<Trash2 size={15} />
							</button>
						</div>
						<span className={styles.hint}>Teklif ve siparişlerini yönetebileceği kişiler</span>
						<UserPicker
							users={users}
							selectedIds={map[delegateId] ?? []}
							excludeIds={[delegateId]}
							onToggle={(userId) => togglePrincipal(delegateId, userId)}
							placeholder="Yetkili olacağı kişiyi ara..."
							hideInactive
						/>
					</div>
				);
			})}
			<UserPicker
				users={users}
				selectedIds={[]}
				excludeIds={delegateIds}
				onToggle={(userId) => update({ ...map, [userId]: [] })}
				placeholder="Vekil ekle..."
				hideInactive
			/>
		</div>
	);
}

function UserPicker({
	users,
	selectedIds,
	onToggle,
	excludeIds = [],
	placeholder,
	hideInactive = false,
}: {
	users: AdminUserRow[];
	selectedIds: string[];
	onToggle: (userId: string) => void;
	excludeIds?: string[];
	placeholder?: string;
	hideInactive?: boolean;
}) {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");

	const nameOf = (u: AdminUserRow) => `${u.firstName} ${u.lastName}`.trim();
	const selected = selectedIds
		.map((id) => users.find((u) => u.id === id))
		.filter((u): u is AdminUserRow => !!u);

	const normalized = query.toLocaleLowerCase("tr");
	const matches = normalized
		? users.filter(
				(u) =>
					!selectedIds.includes(u.id) &&
					!excludeIds.includes(u.id) &&
					(!hideInactive || u.isActive) &&
					(nameOf(u).toLocaleLowerCase("tr").includes(normalized) ||
						u.email.toLocaleLowerCase("tr").includes(normalized)),
			)
		: [];

	return (
		<div className={styles.multiSelect}>
			{selected.length > 0 && (
				<div className={styles.chipRow}>
					{selected.map((u) => (
						<span key={u.id} className={styles.chip}>
							{u.isActive ? nameOf(u) : `${nameOf(u)} (${t("inactive")})`}
							<button
								type="button"
								className={styles.chipRemove}
								onClick={() => onToggle(u.id)}
								aria-label={t("remove")}
							>
								<X size={13} />
							</button>
						</span>
					))}
				</div>
			)}
			<input
				type="text"
				className={styles.input}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder={placeholder ?? t("searchUserPlaceholder")}
			/>
			{normalized && (
				<div className={styles.optionList}>
					{matches.length === 0 ? (
						<span className={styles.hint}>{t("noRecords")}</span>
					) : (
						matches.slice(0, 30).map((u) => (
							<button
								key={u.id}
								type="button"
								className={styles.option}
								onClick={() => {
									onToggle(u.id);
									setQuery("");
								}}
							>
								<span>{nameOf(u)}</span>
								<span className={styles.optionMeta}>{u.email}</span>
							</button>
						))
					)}
				</div>
			)}
		</div>
	);
}

interface RegionMemberRow {
	userId: string;
	firstName: string;
	lastName: string;
	email: string;
	isLeader: boolean;
}

interface RegionRow {
	id: string;
	name: string;
	description: string | null;
	isActive: boolean;
	managers: RegionMemberRow[];
	members: RegionMemberRow[];
}

/** Bölge müdürlükleri: müdürler kendi bölgesindeki çalışanların aktivitelerini görebilir. */
function RegionsSection({ users }: { users: AdminUserRow[] }) {
	const { t } = useTranslation();
	const [regions, setRegions] = useState<RegionRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [status, setStatus] = useState<TestResult | null>(null);
	const [newName, setNewName] = useState("");
	const [creating, setCreating] = useState(false);

	const reload = async () => {
		try {
			const res = await api.get<{ items: RegionRow[] }>("/api/admin/regions");
			setRegions(res.items ?? []);
		} catch (err) {
			setStatus({ type: "error", message: err instanceof Error ? err.message : t("error") });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		let cancelled = false;
		api
			.get<{ items: RegionRow[] }>("/api/admin/regions")
			.then((res) => {
				if (!cancelled) setRegions(res.items ?? []);
			})
			.catch(() => {
				if (!cancelled) setRegions([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const createRegion = async () => {
		const name = newName.trim();
		if (!name || creating) return;
		setCreating(true);
		setStatus(null);
		try {
			await api.post("/api/admin/regions", { name, managerIds: [], memberIds: [] });
			setNewName("");
			await reload();
		} catch (err) {
			setStatus({ type: "error", message: err instanceof Error ? err.message : t("error") });
		} finally {
			setCreating(false);
		}
	};

	const patchRegion = async (region: RegionRow, body: Record<string, unknown>) => {
		setStatus(null);
		try {
			const updated = await api.patch<RegionRow>(`/api/admin/regions/${region.id}`, body);
			setRegions((prev) => prev.map((r) => (r.id === region.id ? updated : r)));
			setStatus({ type: "success", message: t("saveSuccess") });
		} catch (err) {
			setStatus({ type: "error", message: err instanceof Error ? err.message : t("error") });
		}
	};

	const removeRegion = async (region: RegionRow) => {
		if (!window.confirm(t("regionDeleteConfirm", { name: region.name }))) return;
		setStatus(null);
		try {
			await api.delete(`/api/admin/regions/${region.id}`);
			setRegions((prev) => prev.filter((r) => r.id !== region.id));
		} catch (err) {
			setStatus({ type: "error", message: err instanceof Error ? err.message : t("error") });
		}
	};

	const toggleId = (ids: string[], id: string) =>
		ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id];

	return (
		<section className={styles.section}>
			<h2 className={styles.sectionTitle}>{t("regionsTitle")}</h2>
			<span className={styles.hint}>{t("regionsHint")}</span>

			{status && (
				<div
					className={`${styles.statusBanner} ${status.type === "success" ? styles.statusSuccess : styles.statusError}`}
				>
					{status.message}
				</div>
			)}

			<div className={styles.fields}>
				{loading ? (
					<span className={styles.hint}>{t("loading")}</span>
				) : regions.length === 0 ? (
					<span className={styles.hint}>{t("noRecords")}</span>
				) : (
					regions.map((region) => {
						const managerIds = region.managers.map((m) => m.userId);
						const memberIds = region.members.map((m) => m.userId);
						return (
							<div key={region.id} className={styles.field}>
								<label className={styles.label}>
									{region.name}
									<span className={styles.hint}>{t("regionManagersLabel")}</span>
								</label>
								<div className={styles.inputWrap}>
									<UserPicker
										users={users}
										selectedIds={managerIds}
										excludeIds={memberIds}
										onToggle={(userId) =>
											void patchRegion(region, { managerIds: toggleId(managerIds, userId) })
										}
										placeholder={t("regionManagerPlaceholder")}
										hideInactive
									/>
									<button
										type="button"
										className={styles.chipRemove}
										onClick={() => void removeRegion(region)}
										title={t("delete")}
										aria-label={t("delete")}
									>
										<Trash2 size={16} />
									</button>
								</div>
								<label className={styles.label}>
									<span className={styles.hint}>{t("regionMembersLabel")}</span>
								</label>
								<div className={styles.inputWrap}>
									<UserPicker
										users={users}
										selectedIds={memberIds}
										excludeIds={managerIds}
										onToggle={(userId) =>
											void patchRegion(region, { memberIds: toggleId(memberIds, userId) })
										}
										placeholder={t("regionMemberPlaceholder")}
										hideInactive
									/>
								</div>
							</div>
						);
					})
				)}
			</div>

			<div className={styles.testRow}>
				<input
					type="text"
					className={styles.input}
					value={newName}
					onChange={(e) => setNewName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") void createRegion();
					}}
					placeholder={t("regionNamePlaceholder")}
				/>
				<button
					type="button"
					className={styles.testButton}
					onClick={() => void createRegion()}
					disabled={creating || newName.trim().length === 0}
				>
					<Plus size={14} />
					{t("regionAdd")}
				</button>
			</div>
		</section>
	);
}
